import React, { useState, useCallback } from "react";
import ServiceSidebar from "./components/ServiceSidebar";
import ServiceEditor from "./components/ServiceEditor";
import { useServices } from "./hooks/useServices";
import { serviceDefaults } from "../../../models/service";
import { ConfirmDialog, MasterDetailLayout, Breadcrumb } from "../../../components/Common";

/**
 * Services Page (Módulo de Produtos e Serviços)
 * Layout Master-Detail: Listagem lateral e editor principal.
 */
const ServicesPage = () => {
    const { items, loading, isSaving, handleSave, handleDelete, handleReorder } = useServices();
    const [selectedItem, setSelectedItem] = useState(null);
    const [isDirty, setIsDirty] = useState(false);

    // Seleção inicial automática
    React.useEffect(() => {
        if (!selectedItem && items.length > 0 && !loading) {
            setSelectedItem(items[0]);
        }
    }, [items, loading, selectedItem]);

    const handleSelectItem = useCallback(async (item) => {
        if (selectedItem?.id === item.id) return;

        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Você tem alterações pendentes. Deseja descartá-las?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }

        setSelectedItem(item);
        setIsDirty(false);
    }, [selectedItem, isDirty]);

    const handleCreateNew = useCallback(async () => {
        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças atuais e criar um novo item?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }
        setSelectedItem(serviceDefaults);
        setIsDirty(false);
    }, [isDirty]);

    const handleSaveItem = useCallback(async (data) => {
        const result = await handleSave(data);
        if (result) {
            setIsDirty(false);
        }
    }, [handleSave]);

    const handleDeleteItem = useCallback(async (id, name) => {
        await handleDelete(id, name);
        setSelectedItem(null);
        setIsDirty(false);
    }, [handleDelete]);

    const handleDirtyChange = useCallback((dirty) => setIsDirty(dirty), []);

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Produtos e Serviços" />
            </div>

            <div className="border-top mb-3"></div>

            <div className="services-container mt-3">
                <MasterDetailLayout
                master={
                    <ServiceSidebar
                        items={items}
                        activeId={selectedItem?.id}
                        onSelect={handleSelectItem}
                        onCreateNew={handleCreateNew}
                        loading={loading}
                        onReorder={handleReorder}
                    />
                }
                detail={
                    <ServiceEditor
                        item={selectedItem}
                        onSave={handleSaveItem}
                        onDelete={handleDeleteItem}
                        isSaving={isSaving}
                        onChange={handleDirtyChange}
                    />
                }
            />
            </div>
        </React.Fragment>
    );
};

export default ServicesPage;
